<script setup>
import { computed, ref, onMounted, watch } from "vue";
import { useQRStore } from "../stores/qr";
import { useToast } from "vue-toastification";

// Component imports
import ContentTypeTabs from "../components/forms/ContentTypeTabs.vue";
import TextForm from "../components/forms/TextForm.vue";
import URLForm from "../components/forms/URLForm.vue";
import VCardForm from "../components/forms/VCardForm.vue";
import WiFiForm from "../components/forms/WiFiForm.vue";
import SMSForm from "../components/forms/SMSForm.vue";
import EmailForm from "../components/forms/EmailForm.vue";
import QROptions from "../components/qr/QROptions.vue";
import StyleOptions from "../components/qr/StyleOptions.vue";
import LogoUpload from "../components/upload/LogoUpload.vue";
import GenerateButton from "../components/qr/GenerateButton.vue";
import QRDisplay from "../components/qr/QRDisplay.vue";
import DownloadOptions from "../components/qr/DownloadOptions.vue";
import GenerationInfo from "../components/qr/GenerationInfo.vue";
import QRHistory from "../components/history/QRHistory.vue";
import ServiceStatus from "../components/qr/ServiceStatus.vue";
import GenerationPreferences from "../components/qr/GenerationPreferences.vue";

import { ChevronDownIcon } from "@heroicons/vue/24/outline";

// Panel visibility state
const showContentPanel = ref(true); // Start expanded
const showQRDisplay = ref(true); // Start expanded
const showLogoUpload = ref(false); // Collapsed by default
const showStyleOptions = ref(false); // Collapsed by default
const showQROptions = ref(false); // Collapsed by default
const showHistory = ref(false); // Collapsed by default

// Composables
const qrStore = useQRStore();
const toast = useToast();

// Local state
const showAdvancedOptions = ref(false);
const showStylePreview = ref(false);
const realtimePreview = ref(false);

// Computed properties from store (read-only)
const formData = computed(() => qrStore.formData);
const logoFile = computed(() => qrStore.logoFile);
const iconFile = computed(() => qrStore.iconFile);
const currentQR = computed(() => qrStore.currentQR);
const isGenerating = computed(() => qrStore.isGenerating);
const canGenerate = computed(() => qrStore.canGenerate);
const generationHistory = computed(() => qrStore.generationHistory);
const serviceStatus = computed(() => qrStore.serviceStatus);
const shouldUseFrontend = computed(() => qrStore.shouldUseFrontend);
const generationSource = computed(() => qrStore.generationSource);
const availablePresets = computed(() => qrStore.availablePresets);

// Enhanced models with getter/setter for v-model compatibility
const qrOptionsModel = computed({
  get: () => qrStore.qrOptions,
  set: (value) => qrStore.updateQROptions(value),
});

const styleOptionsModel = computed({
  get: () => qrStore.styleOptions,
  set: (value) => qrStore.updateStyleOptions(value),
});

const preferencesModel = computed({
  get: () => qrStore.preferences,
  set: (value) => qrStore.updatePreferences(value),
});

// Active tab model for ContentTypeTabs v-model
const activeTabModel = computed({
  get() {
    return qrStore.formData.activeTab;
  },
  set(value) {
    qrStore.formData.activeTab = value;
  },
});

// Form data models with proper reactivity
const textModel = computed({
  get: () => qrStore.formData.text,
  set: (value) => {
    qrStore.formData.text = value;
    if (realtimePreview.value && value.trim()) {
      //debouncedGenerate()
    }
  },
});

const urlModel = computed({
  get: () => qrStore.formData.url,
  set: (value) => {
    qrStore.formData.url = value;
    if (realtimePreview.value && value.trim()) {
      //debouncedGenerate()
    }
  },
});

const vCardModel = computed({
  get: () => qrStore.formData.contact,
  set: (value) => {
    Object.assign(qrStore.formData.contact, value);
    if (realtimePreview.value) {
      //debouncedGenerate()
    }
  },
});

const wifiModel = computed({
  get: () => qrStore.formData.wifi,
  set: (value) => {
    Object.assign(qrStore.formData.wifi, value);
    if (realtimePreview.value) {
      //debouncedGenerate()
    }
  },
});

const smsModel = computed({
  get: () => qrStore.formData.sms,
  set: (value) => {
    Object.assign(qrStore.formData.sms, value);
    if (realtimePreview.value) {
      //debouncedGenerate()
    }
  },
});

const emailModel = computed({
  get: () => qrStore.formData.email,
  set: (value) => {
    Object.assign(qrStore.formData.email, value);
    if (realtimePreview.value) {
      //debouncedGenerate()
    }
  },
});

const logoModel = computed({
  get: () => qrStore.logoFile,
  set: (value) => qrStore.setLogoFile(value),
});

const iconModel = computed({
  get: () => qrStore.iconFile,
  set: (value) => qrStore.setIconFile(value),
});

// Debounced generation for realtime preview
let generateTimeout = null;
const debouncedGenerate = () => {
  if (!qrStore.preferences.instantPreview) return;

  clearTimeout(generateTimeout);
  generateTimeout = setTimeout(() => {
    if (canGenerate.value && shouldUseFrontend.value) {
      handleGenerate(true); // Silent generation
    }
  }, 500);
};

// Event handlers
const handleTabChange = (newTab) => {
  qrStore.formData.activeTab = newTab;
};

const handleOptionsUpdate = (newOptions) => {
  qrStore.updateQROptions(newOptions);
  if (realtimePreview.value && shouldUseFrontend.value) {
    //debouncedGenerate();
  }
};

const handleStyleOptionsUpdate = (newStyleOptions) => {
  qrStore.updateStyleOptions(newStyleOptions);
  if (realtimePreview.value && shouldUseFrontend.value) {
    //debouncedGenerate();
  }
};

const handlePreferencesUpdate = (newPreferences) => {
  qrStore.updatePreferences(newPreferences);
  realtimePreview.value = newPreferences.instantPreview;
};

const handleLogoUpload = (file) => {
  qrStore.setLogoFile(file);
  // Logo requires backend, so generate immediately
  if (canGenerate.value) {
    handleGenerate();
  }
};

const handleIconUpload = (file) => {
  qrStore.setIconFile(file);
  // Icon requires backend, so generate immediately
  if (canGenerate.value) {
    handleGenerate();
  }
};

const handleLogoRemove = () => {
  qrStore.removeLogoFile();
  if (realtimePreview.value && canGenerate.value) {
    //debouncedGenerate();
  }
};

const handleIconRemove = () => {
  qrStore.removeIconFile();
  if (realtimePreview.value && canGenerate.value) {
    //debouncedGenerate();
  }
};

const handleGenerate = async (silent = false) => {
  try {
    await qrStore.generateQR();
    if (!silent) {
      toast.success(`QR generated using ${generationSource.value}!`);
    }
  } catch (error) {
    console.error("Generation failed:", error);
    if (!silent) {
      toast.error(error.message || "Generation failed");
    }
  }
};

const handleQuickStyle = async (styleType) => {
  try {
    await qrStore.generateWithQuickStyle(styleType);
  } catch (error) {
    console.error("Quick style failed:", error);
    toast.error(error.message || "Quick style failed");
  }
};

const handlePresetApply = (presetName) => {
  qrStore.applyPreset(presetName);
  if (canGenerate.value) {
    if (shouldUseFrontend.value && realtimePreview.value) {
      //debouncedGenerate();
    } else {
      handleGenerate();
    }
  }
};

const handleRegenerateWithFrontend = async () => {
  try {
    await qrStore.regenerateWithFrontend();
  } catch (error) {
    console.error("Frontend regeneration failed:", error);
    toast.error("Frontend regeneration failed");
  }
};

const handleRegenerateWithBackend = async () => {
  try {
    await qrStore.regenerateWithBackend();
  } catch (error) {
    console.error("Backend regeneration failed:", error);
    toast.error("Backend regeneration failed");
  }
};

const handleDownload = async (format = "png") => {
  try {
    await qrStore.downloadQR(format);
  } catch (error) {
    console.error("Download failed:", error);
    toast.error("Download failed");
  }
};

const handleRegenerate = (item) => {
  qrStore.regenerateFromHistory(item);
  toast.success("QR code regenerated from history");
};

const handleDeleteFromHistory = (id) => {
  qrStore.deleteFromHistory(id);
};

const handleClearHistory = () => {
  qrStore.clearHistory();
};

// Form validation helpers
const validateCurrentForm = () => {
  switch (formData.value.activeTab) {
    case "text":
      return textModel.value?.trim()?.length > 0;
    case "url":
      try {
        new URL(urlModel.value);
        return true;
      } catch {
        return false;
      }
    case "vcard": {
      const hasName =
        vCardModel.value?.name?.trim()?.length > 0 ||
        vCardModel.value?.firstName?.trim()?.length > 0;
      if (!hasName) return false;
      const email = vCardModel.value?.email?.trim();
      if (email) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
      }
      const website = vCardModel.value?.website?.trim();
      if (website) {
        try {
          new URL(website.startsWith("http") ? website : "https://" + website);
        } catch {
          return false;
        }
      }
      return true;
    }
    case "wifi":
      return wifiModel.value?.ssid?.trim()?.length > 0;
    case "sms":
      return smsModel.value?.number?.trim()?.length > 0;
    case "email":
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(emailModel.value?.address || "");
    default:
      return false;
  }
};

const isFormValid = computed(() => validateCurrentForm());

// Lifecycle
onMounted(async () => {
  qrStore.initializeSession();
  await qrStore.checkServiceStatus();
  realtimePreview.value = qrStore.preferences.instantPreview;
});

// Watch for style changes and trigger preview
watch(
  () => qrStore.styleOptions,
  (newStyle, oldStyle) => {
    if (realtimePreview.value && shouldUseFrontend.value && canGenerate.value) {
      // Only trigger if meaningful changes
      const meaningfulChange =
        JSON.stringify(newStyle) !== JSON.stringify(oldStyle);
      if (meaningfulChange) {
        //debouncedGenerate();
      }
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="container mx-auto px-4 py-8">
    <div class="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden">
      <!-- Header: stacked on mobile, inline on tablet/desktop -->
      <div class="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 tablet:p-8 text-center relative">
        <h1 class="text-2xl tablet:text-4xl font-bold mb-2">Hybrid QR Code Generator</h1>
        <p class="text-base tablet:text-xl opacity-90 mb-4">
          Frontend styling + Backend processing = Best of both worlds
        </p>

        <!-- Service status + badge: stack on mobile to avoid wrap -->
        <div class="flex flex-col gap-2 tablet:flex-row tablet:items-center tablet:justify-center tablet:gap-4">
          <ServiceStatus :status="serviceStatus" class="tablet:absolute tablet:top-4 tablet:right-4" />
          <div
            class="inline-flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium min-h-[44px] tablet:min-h-0"
            :class="
              generationSource === 'frontend'
                ? 'bg-green-100 text-green-800'
                : 'bg-blue-100 text-blue-800'
            "
          >
            <span
              class="w-2 h-2 rounded-full mr-2 shrink-0"
              :class="
                generationSource === 'frontend' ? 'bg-green-400' : 'bg-blue-400'
              "
            />
            Using {{ generationSource }}
          </div>
        </div>
      </div>

      <!-- Generation Preferences (touch-friendly on mobile) -->
      <div class="border-b border-gray-200 px-4 tablet:px-8 py-4 bg-gray-50 min-h-[44px] flex items-center">
        <GenerationPreferences
          v-model="preferencesModel"
          :service-status="serviceStatus"
          @update:modelValue="handlePreferencesUpdate"
        />
      </div>

      <!-- Content Type Tabs Section -->
      <div class="p-4 tablet:p-8 pb-4">
        <div class="bg-white rounded-lg border shadow-sm">
          <div class="p-4 border-b">
            <h3 class="text-lg font-semibold text-gray-900">Content Type</h3>
          </div>
          <div class="p-4">
            <ContentTypeTabs v-model="activeTabModel" />
          </div>
        </div>
      </div>

      <!-- Flex Gallery Layout: 2-col tablet/desktop, 1-col mobile -->
      <div class="flex-gallery-container p-4">
        
        <!-- Content Forms Panel -->
        <div class="panel-card" :class="{ 'panel-expanded': showContentPanel }">
          <button
            @click="showContentPanel = !showContentPanel"
            class="panel-header min-h-[44px] flex items-center"
          >
            <div class="flex items-center justify-between w-full">
              <div>
                <h3 class="text-xl font-semibold text-slate-900 mb-1">Content</h3>
                <p class="text-slate-600 text-sm">Enter your data for QR code generation</p>
              </div>
              <div class="flex items-center gap-3">
                <div class="flex items-center gap-2">
                  <div class="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span class="text-xs text-slate-600 font-medium capitalize">
                    {{ formData?.activeTab || "text" }}
                  </span>
                </div>
                <ChevronDownIcon
                  class="w-5 h-5 text-slate-400 transition-transform duration-200"
                  :class="{ 'rotate-180': showContentPanel }"
                />
              </div>
            </div>
          </button>

          <div v-if="showContentPanel" class="panel-content">
            <div class="space-y-4">
              <TextForm v-if="formData?.activeTab === 'text'" v-model="textModel" />
              <URLForm v-if="formData?.activeTab === 'url'" v-model="urlModel" />
              <VCardForm v-if="formData?.activeTab === 'vcard'" v-model="vCardModel" :backend-errors="qrStore.vcardValidationErrors" />
              <WiFiForm v-if="formData?.activeTab === 'wifi'" v-model="wifiModel" />
              <SMSForm v-if="formData?.activeTab === 'sms'" v-model="smsModel" />
              <EmailForm v-if="formData?.activeTab === 'email'" v-model="emailModel" />
            </div>
          </div>
        </div>

        <!-- QR Display Panel -->
        <div class="panel-card" :class="{ 'panel-expanded': showQRDisplay }">
          <button @click="showQRDisplay = !showQRDisplay" class="panel-header min-h-[44px] flex items-center">
            <div class="flex items-center justify-between w-full">
              <div>
                <h3 class="text-xl font-semibold text-slate-900 mb-1">Generated QR Code</h3>
                <p class="text-slate-600 text-sm">Your QR code will appear here</p>
              </div>
              <div class="flex items-center gap-3">
                <div class="flex items-center gap-2">
                  <div 
                    class="w-2 h-2 rounded-full"
                    :class="{
                      'bg-amber-500 animate-pulse': isGenerating,
                      'bg-emerald-500': currentQR && !isGenerating,
                      'bg-slate-300': !currentQR && !isGenerating
                    }"
                  ></div>
                  <span class="text-xs text-slate-600 font-medium">
                    {{ isGenerating ? 'Generating...' : currentQR ? 'Ready' : 'No QR' }}
                  </span>
                </div>
                <ChevronDownIcon
                  class="w-5 h-5 text-slate-400 transition-transform duration-200"
                  :class="{ 'rotate-180': showQRDisplay }"
                />
              </div>
            </div>
          </button>
          
          <div v-if="showQRDisplay" class="panel-content">
            <QRDisplay
              :qr-code="currentQR"
              :loading="isGenerating"
              :realtime-preview="realtimePreview"
              @download="handleDownload"
            />
            
            <!-- Download Options (inline when QR exists) -->
            <div v-if="currentQR" class="mt-4 pt-4 border-t border-slate-100">
              <h4 class="text-sm font-semibold text-gray-900 mb-3">Download Options</h4>
              <DownloadOptions
                :qr-code-data="currentQR.qrCode || currentQR.qrCodeData || currentQR.data.qrCode"
                :filename="`qrcode-${formData.activeTab}`"
                :options="qrOptionsModel"
                :source="currentQR.source || generationSource"
                @download="handleDownload"
              />
            </div>

            <!-- Generation Info (inline when QR exists) -->
            <div v-if="currentQR" class="mt-4 pt-4 border-t border-slate-100">
              <GenerationInfo
                :qr-data="currentQR"
                :is-generating="isGenerating"
                :generation-source="generationSource"
                :service-status="serviceStatus"
              />
            </div>
          </div>
        </div>


        <!-- Style Options Panel -->
        <div class="panel-card" :class="{ 'panel-expanded': showStyleOptions }">
          <button @click="showStyleOptions = !showStyleOptions" class="panel-header min-h-[44px] flex items-center">
            <div class="flex items-center justify-between w-full">
              <div>
                <h3 class="text-xl font-semibold text-slate-900 mb-1">Style Options</h3>
                <p class="text-slate-600 text-sm">Colors, patterns, and visual presets</p>
              </div>
              <div class="flex items-center gap-3">
                <div class="flex items-center space-x-2">
                  <span class="text-sm text-slate-600">Frontend Styling</span>
                  <div
                    class="w-2 h-2 rounded-full"
                    :class="shouldUseFrontend ? 'bg-green-400' : 'bg-gray-400'"
                  ></div>
                </div>
                <ChevronDownIcon
                  class="w-5 h-5 text-slate-400 transition-transform duration-200"
                  :class="{ 'rotate-180': showStyleOptions }"
                />
              </div>
            </div>
          </button>

          <div v-if="showStyleOptions" class="panel-content">
            <StyleOptions
              v-model="styleOptionsModel"
              :available-presets="availablePresets"
              :frontend-available="serviceStatus?.frontend?.available"
              @preset-apply="handlePresetApply"
              @quick-style="handleQuickStyle"
            />
          </div>
        </div>

        <!-- QR Options Panel -->
        <div class="panel-card" :class="{ 'panel-expanded': showQROptions }">
          <button @click="showQROptions = !showQROptions" class="panel-header min-h-[44px] flex items-center">
            <div class="flex items-center justify-between w-full">
              <div>
                <h3 class="text-xl font-semibold text-slate-900 mb-1">QR Options</h3>
                <p class="text-slate-600 text-sm">Error correction, size, format settings</p>
              </div>
              <div class="flex items-center gap-3">
                <div class="text-xs text-slate-500 text-right">
                  <div>{{ qrOptionsModel?.errorCorrectionLevel }}</div>
                  <div>{{ qrOptionsModel?.size }}px</div>
                </div>
                <ChevronDownIcon
                  class="w-5 h-5 text-slate-400 transition-transform duration-200"
                  :class="{ 'rotate-180': showQROptions }"
                />
              </div>
            </div>
          </button>

          <div v-if="showQROptions" class="panel-content">
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <button
                  @click="showAdvancedOptions = !showAdvancedOptions"
                  class="text-sm text-blue-600 hover:text-blue-800"
                >
                  {{ showAdvancedOptions ? "Hide" : "Show" }} Advanced
                </button>
              </div>

              <QROptions
                v-model="qrOptionsModel"
                :show-advanced="showAdvancedOptions"
                :generation-source="generationSource"
                @update:modelValue="handleOptionsUpdate"
              />
            </div>
          </div>
        </div>

        
        <!-- Logo/Icon Upload Panel -->
        <div class="panel-card" :class="{ 'panel-expanded': showLogoUpload }">
          <button @click="showLogoUpload = !showLogoUpload" class="panel-header min-h-[44px] flex items-center">
            <div class="flex items-center justify-between w-full">
              <div>
                <h3 class="text-xl font-semibold text-slate-900 mb-1">Logo & Icons</h3>
                <p class="text-slate-600 text-sm">Upload custom logos and icons</p>
              </div>
              <div class="flex items-center gap-3">
                <span class="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                  Backend Only
                </span>
                <ChevronDownIcon
                  class="w-5 h-5 text-slate-400 transition-transform duration-200"
                  :class="{ 'rotate-180': showLogoUpload }"
                />
              </div>
            </div>
          </button>

          <div v-if="showLogoUpload" class="panel-content">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <LogoUpload
                v-model="logoModel"
                label="Logo Upload"
                @upload="handleLogoUpload"
                @remove="handleLogoRemove"
              />
              <!-- <LogoUpload
                v-model="iconModel"
                label="Icon Upload"
                @upload="handleIconUpload"
                @remove="handleIconRemove"
              /> -->
            </div>
          </div>
        </div>

        <!-- QR History Panel -->
        <div class="panel-card panel-full-width" :class="{ 'panel-expanded': showHistory }">
          <button @click="showHistory = !showHistory" class="panel-header min-h-[44px] flex items-center">
            <div class="flex items-center justify-between w-full">
              <div>
                <h3 class="text-xl font-semibold text-slate-900 mb-1">Recent QR Codes</h3>
                <p class="text-slate-600 text-sm">
                  View and manage your previously generated QR codes ({{ generationHistory?.length || 0 }} items)
                </p>
              </div>
              <ChevronDownIcon
                class="w-5 h-5 text-slate-400 transition-transform duration-200"
                :class="{ 'rotate-180': showHistory }"
              />
            </div>
          </button>

          <div v-if="showHistory" class="panel-content">
            <QRHistory
              :history="generationHistory"
              :show-source="true"
              @regenerate="handleRegenerate"
              @delete="handleDeleteFromHistory"
              @clear="handleClearHistory"
            />
          </div>
        </div>

      </div>

      <!-- Floating Generate Button -->
      <div class="floating-generate-button">
        <div class="relative">
          <div class="bg-white rounded-2xl shadow-lg border border-slate-200 p-1">
            <GenerateButton
              :loading="isGenerating"
              :disabled="!canGenerate || !isFormValid"
              :generation-source="generationSource"
              :realtime-preview="realtimePreview"
              @click="handleGenerate"
              @regenerate-frontend="handleRegenerateWithFrontend"
              @regenerate-backend="handleRegenerateWithBackend"
              class="rounded-xl"
            />
          </div>
          
          <!-- Status Indicator -->
          <div
            v-if="isGenerating"
            class="absolute -top-2 -right-2 w-4 h-4 bg-indigo-500 rounded-full animate-pulse"
          >
            <div class="absolute inset-0 bg-indigo-500 rounded-full animate-ping"></div>
          </div>
          
          <!-- Validation Indicator -->
          <div
            v-else-if="!isFormValid"
            class="absolute -top-2 -right-2 w-4 h-4 bg-amber-500 rounded-full"
            title="Form validation required"
          ></div>
          
          <!-- Ready Indicator -->
          <div
            v-else-if="canGenerate"
            class="absolute -top-2 -right-2 w-4 h-4 bg-emerald-500 rounded-full"
            title="Ready to generate"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Container styles */
.container {
  max-width: 1400px;
}

/* Flex Gallery Layout */
.flex-gallery-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-start;
  justify-content: flex-start;
}

/* Panel Card Base Styles */
.panel-card {
  background: white;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex: 0 0 auto;
  min-width: calc(50% - 0.5rem);
  max-width: calc(50% - 0.5rem);
}

/* Panel sizing based on state */
.panel-card:not(.panel-expanded) {
  flex: 0 0 calc(50% - 0.5rem);
  height: fit-content;
}

.panel-card.panel-expanded {
  flex: 0 0 calc(50% - 0.5rem);
  min-width: calc(50% - 0.5rem);
}

.panel-full-width {
  flex: 1 1 100%;
  width: 100%;
  min-width: 100%;
  max-width: 100%;
}

/* Panel Header */
.panel-header {
  width: 100%;
  padding: 1.5rem;
  text-align: left;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.panel-header:hover {
  background: #f1f5f9;
}

/* Panel Content */
.panel-content {
  padding: 1.5rem;
  background: white;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive: single column only on mobile (< 640px); tablet/desktop keep 2-col */
@media (max-width: 639px) {
  .flex-gallery-container {
    flex-direction: column;
  }

  .panel-card,
  .panel-card.panel-expanded {
    flex: 1 1 auto;
    min-width: 100%;
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .panel-content {
    padding: 1rem;
  }

  .panel-header {
    padding: 1rem;
  }
}

/* Floating Generate Button */
.floating-generate-button {
  position: fixed;
  top: 50vh;
  right: 2rem;
  transform: translateY(-50%);
  z-index: 1000;
  transition: all 0.3s ease;
}

.floating-generate-button:hover {
  transform: translateY(-50%) translateX(-8px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Enhanced floating button styling */
.floating-generate-button .bg-white {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Mobile: bottom-right with safe-area so it doesn't overlap content or notches */
@media (max-width: 639px) {
  .floating-generate-button {
    top: auto;
    bottom: max(1rem, env(safe-area-inset-bottom, 1rem));
    right: max(1rem, env(safe-area-inset-right, 1rem));
    left: auto;
    transform: none;
  }

  .floating-generate-button:hover {
    transform: translateY(-4px);
  }
}

/* Enhanced hover effects */
.panel-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
}

/* Service status indicator styles */
.service-status {
  transition: all 0.3s ease;
}

/* Generation source badge animation */
.generation-source {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

/* Real-time preview indicator */
.realtime-indicator {
  position: relative;
}

.realtime-indicator::after {
  content: "";
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
  animation: pulse 1s infinite;
}

/* Loading states */
.loading-skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Enhanced form validation styles */
.form-invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.form-valid {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

/* Frontend/Backend indicator styles */
.source-frontend {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.source-backend {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
}

/* Advanced options transition */
.advanced-options-enter-active,
.advanced-options-leave-active {
  transition: all 0.3s ease;
}

.advanced-options-enter-from,
.advanced-options-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Status indicator animations */
@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Focus styles for accessibility */
button:focus-visible {
  outline: 2px solid #4f46e5;
  outline-offset: 2px;
}

/* Panel specific adjustments */
.panel-card {
  /* Masonry-like behavior for better space utilization */
  break-inside: avoid;
  page-break-inside: avoid;
}

/* Grid fallback for older browsers */
@supports not (display: flex) {
  .flex-gallery-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem;
  }
}

/* Enhanced panel states */
.panel-card.panel-expanded {
  /* Ensure expanded panels have enough space */
  min-height: 200px;
}

.panel-card:not(.panel-expanded) {
  /* Collapsed panels should be compact */
  height: auto;
  min-height: auto;
}

/* Smooth transitions for all interactive elements */
.panel-card,
.panel-header,
.panel-content {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Enhanced spacing for better visual hierarchy */
.panel-content > * + * {
  margin-top: 1rem;
}

/* Badge styling */
.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Color-coded status indicators */
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 0.5rem;
}

.status-generating {
  background-color: #f59e0b;
  animation: pulse 2s infinite;
}

.status-ready {
  background-color: #10b981;
}

.status-empty {
  background-color: #94a3b8;
}

.status-frontend {
  background-color: #10b981;
}

.status-backend {
  background-color: #6366f1;
}

/* Improved spacing for mobile */
@media (max-width: 640px) {
  .flex-gallery-container {
    padding: 0.5rem;
    gap: 0.75rem;
  }
  
  .panel-header {
    padding: 1rem;
  }
  
  .panel-content {
    padding: 1rem;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .panel-card {
    border-width: 2px;
    border-color: #000;
  }
  
  .panel-header {
    border-bottom-width: 2px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .panel-card,
  .panel-header,
  .panel-content,
  .floating-generate-button {
    transition: none;
    animation: none;
  }
  
  .slideDown {
    animation: none;
  }
}

/* Print styles */
@media print {
  .floating-generate-button {
    display: none;
  }
  
  .panel-card {
    box-shadow: none;
    border: 1px solid #000;
    break-inside: avoid;
  }
  
  .flex-gallery-container {
    display: block;
  }
  
  .panel-content {
    display: block !important;
  }
}
</style>